import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Users, Search, Eye, EyeOff, Check, X, AlertTriangle, KeyRound } from 'lucide-react';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';

// Engine-level Drivers directory — mirrors the Google Sheet "Drivers" tab. Core fields
// are visible to all site users; banking/PIN are fetched on demand and only for admins
// (getDriverSensitive is admin-gated server-side too). Area column doubles as the
// inline "assign area" control and flags anyone still Unassigned.
const API = '/.netlify/functions/drivers-sb';
const post = (body) => fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((r) => r.json());

const AREA_LABEL = { DARL: 'Darlinghurst', BWAY: 'Broadway', CITY: 'City' };
const AREA_COLOR = { DARL: '#8b5cf6', BWAY: '#2563eb', CITY: '#0d9488' };
const AREA_OPTIONS = [['CITY', 'City'], ['BWAY', 'Broadway'], ['DARL', 'Darlinghurst']];

const COLS = [
  { key: 'login', label: 'Login' },
  { key: 'name', label: 'Name' },
  { key: 'company', label: 'Company' },
  { key: 'abn', label: 'ABN' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'email', label: 'Email' },
  { key: 'area', label: 'Area' },
  { key: 'type', label: 'Type' },
  { key: 'active', label: 'Active' },
];

const th = { textAlign: 'left', padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: '.66rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hp-gold)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', borderBottom: '1px solid var(--hp-border)' };
const td = { padding: '9px 12px', fontSize: '.85rem', color: 'var(--hp-text)', borderBottom: '1px solid var(--hp-border)', whiteSpace: 'nowrap' };

const fieldStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-mono)', fontSize: '.9rem', letterSpacing: '.04em' };
const labelStyle = { fontFamily: 'var(--font-mono)', fontSize: '.62rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hp-dim)', marginBottom: 5, display: 'block' };

// Admin-only credential editor. The login is the engine's join key (~40 tables key off
// it with no FK), so changing it goes through the server's renameDriverLogin, which
// migrates every reference in one action. We always preflight a dry run first so the
// blast radius is on screen BEFORE anything is written — a rename with history behind it
// moves already-invoiced work onto a new login, and that should never be a surprise.
function CredentialsModal({ driver, onClose, onRenamed, onPinSaved }) {
  const [login, setLogin] = useState(driver.login);
  const [pin, setPin] = useState('');
  const [impact, setImpact] = useState(null);   // dry-run result for the typed login
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  const nextLogin = login.trim().toUpperCase();
  const loginChanged = nextLogin !== driver.login;
  const pinChanged = pin.trim().length > 0;
  const loginValid = /^[A-Z0-9]{3,20}$/.test(nextLogin);

  // Preflight whenever the typed login settles on something new and valid.
  useEffect(() => {
    if (!loginChanged || !loginValid) { setImpact(null); return; }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const res = await post({ action: 'renameDriverLogin', from: driver.login, to: nextLogin, dryRun: true });
        if (!cancelled) { setImpact(res); setErr(res.success ? '' : (res.error || '')); }
      } catch (e) { if (!cancelled) setErr(e.message); }
      finally { if (!cancelled) setChecking(false); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [nextLogin, loginChanged, loginValid, driver.login]);

  async function save() {
    setErr(''); setSaving(true);
    try {
      if (pinChanged) {
        const res = await post({ action: 'updateDriver', login: driver.login, updates: { pin: pin.trim() } });
        if (!res.success) throw new Error(res.error || 'Could not update the PIN');
        onPinSaved(driver.login);
      }
      if (loginChanged) {
        const res = await post({ action: 'renameDriverLogin', from: driver.login, to: nextLogin, confirm: true });
        if (!res.success) throw new Error(res.error || 'Could not rename the login');
        onRenamed(driver.login, nextLogin, res);
        setDone(`Renamed to ${nextLogin}${res.total > 1 ? ` · ${res.total} records migrated` : ''}`);
        setTimeout(onClose, 1400);
        return;
      }
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  const rows = impact && impact.counts ? Object.entries(impact.counts).sort((a, b) => b[1] - a[1]) : [];
  const canSave = (loginChanged && loginValid && !checking && !(impact && impact.error)) || pinChanged;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 22, width: '100%', maxWidth: 460, maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <KeyRound size={17} color="var(--hp-gold)" />
          <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.05rem' }}>Login credentials</h3>
        </div>
        <div style={{ color: 'var(--hp-dim)', fontSize: '.8rem', marginBottom: 18 }}>{driver.name || driver.login}</div>

        <label style={labelStyle}>Login ID</label>
        <input autoFocus value={login} onChange={(e) => setLogin(e.target.value.toUpperCase())} style={fieldStyle} />
        <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 5, marginBottom: 16 }}>
          Must match the Australia Post scanner login exactly — scans arriving under a different spelling won’t reach this driver.
        </div>

        <label style={labelStyle}>PIN {pinChanged ? '' : '(leave blank to keep current)'}</label>
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" style={{ ...fieldStyle, marginBottom: 16 }} />

        {loginChanged && !loginValid && (
          <div style={{ fontSize: '.78rem', color: 'var(--hp-amber)', marginBottom: 12 }}>
            Login must be 3–20 characters, letters and numbers only.
          </div>
        )}

        {checking && <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 12 }}>Checking what’s linked to {driver.login}…</div>}

        {impact && impact.success && loginValid && (
          impact.total === 0 ? (
            <div style={{ background: 'rgba(31,138,76,.1)', border: '1px solid rgba(31,138,76,.35)', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: '.8rem', color: 'var(--hp-green)' }}>
              Clean rename — {driver.login} has no linked records yet. Nothing to migrate.
            </div>
          ) : (
            <div style={{ background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.35)', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: '.8rem' }}>
              <div style={{ color: 'var(--hp-amber)', fontWeight: 700, marginBottom: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
                <AlertTriangle size={14} /> {impact.total} linked record{impact.total === 1 ? '' : 's'} will move to {nextLogin}
              </div>
              <div style={{ maxHeight: 130, overflowY: 'auto' }}>
                {rows.map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', fontSize: '.7rem', padding: '1px 0' }}>
                    <span>{k}</span><span style={{ color: 'var(--hp-text)', fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '.72rem', marginTop: 7, lineHeight: 1.45 }}>
                They all move together, so nothing is orphaned — but if any of this sits in an
                invoiced period, it will now bill under {nextLogin}.
              </div>
            </div>
          )
        )}

        {err && (
          <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.4)', color: '#ef4444', borderRadius: 8, padding: '9px 12px', marginBottom: 14, fontSize: '.8rem' }}>{err}</div>
        )}
        {done && (
          <div style={{ background: 'rgba(31,138,76,.12)', border: '1px solid rgba(31,138,76,.4)', color: 'var(--hp-green)', borderRadius: 8, padding: '9px 12px', marginBottom: 14, fontSize: '.8rem' }}>{done}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: '.85rem' }}>Cancel</button>
          <button onClick={save} disabled={!canSave || saving || !!done}
            style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: canSave && !saving ? 'var(--hp-gold)' : 'var(--hp-border)', color: canSave && !saving ? '#000' : 'var(--hp-dim)', fontWeight: 700, cursor: canSave && !saving ? 'pointer' : 'not-allowed', fontSize: '.85rem' }}>
            {saving ? 'Saving…' : loginChanged ? 'Rename & save' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DriversPage() {
  const { currentUser } = useApp();
  const isAdmin = currentUser?.role === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // ?q= deep link from the global search palette pre-fills the search box.
  const [searchParams] = useSearchParams();
  const qParam = searchParams.get('q') || '';
  const [query, setQuery] = useState(qParam);
  useEffect(() => { if (qParam) setQuery(qParam); }, [qParam]);
  const [sort, setSort] = useState({ key: 'login', dir: 1 });
  const [busy, setBusy] = useState({});          // login -> true while a row write is in flight
  const [reveal, setReveal] = useState({});      // login -> sensitive object (admin reveal)
  const [editing, setEditing] = useState(null);  // driver row whose credentials are open
  const [photos, setPhotos] = useState({});      // login -> profile pic data URL

  const load = useCallback(() => {
    setLoading(true); setError('');
    post({ action: 'listDrivers' })
      .then((d) => { if (!d.success) throw new Error(d.error || 'Could not load drivers'); setRows(d.drivers || []); })
      .catch((e) => setError(e.message || 'Could not load drivers'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (rows.length) fetchDriverPhotos(rows.map((r) => r.login)).then((p) => setPhotos((prev) => ({ ...p, ...prev })));
  }, [rows]);

  const patchRow = (login, patch) => setRows((rs) => rs.map((r) => (r.login === login ? { ...r, ...patch } : r)));
  const setRowBusy = (login, v) => setBusy((b) => ({ ...b, [login]: v }));

  async function assignArea(login, area) {
    const prev = rows.find((r) => r.login === login)?.area || '';
    patchRow(login, { area });            // optimistic
    setRowBusy(login, true);
    try {
      const res = await post({ action: 'assignArea', login, area });
      if (!res.success) throw new Error(res.error);
      patchRow(login, { area: res.area, route: res.route });
    } catch (e) {
      patchRow(login, { area: prev });    // revert
      setError(`Could not set area for ${login}: ${e.message}`);
    } finally { setRowBusy(login, false); }
  }

  async function toggleActive(login, next) {
    patchRow(login, { active: next });    // optimistic
    setRowBusy(login, true);
    try {
      const res = await post({ action: 'updateDriver', login, updates: { active: next } });
      if (!res.success) throw new Error(res.error);
    } catch (e) {
      patchRow(login, { active: !next }); // revert
      setError(`Could not update ${login}: ${e.message}`);
    } finally { setRowBusy(login, false); }
  }

  async function toggleReveal(login) {
    if (reveal[login]) { setReveal((r) => { const n = { ...r }; delete n[login]; return n; }); return; }
    setRowBusy(login, true);
    try {
      const res = await post({ action: 'getDriverSensitive', login });
      if (!res.success) throw new Error(res.error);
      setReveal((r) => ({ ...r, [login]: res.sensitive }));
    } catch (e) { setError(`Could not reveal ${login}: ${e.message}`); }
    finally { setRowBusy(login, false); }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = rows;
    if (q) out = rows.filter((r) => [r.login, r.name, r.company, r.abn, r.mobile, r.email].some((v) => (v || '').toLowerCase().includes(q)));
    const { key, dir } = sort;
    return [...out].sort((a, b) => {
      let av = a[key], bv = b[key];
      if (key === 'active') { av = av ? 1 : 0; bv = bv ? 1 : 0; }
      av = (av === undefined || av === null) ? '' : av; bv = (bv === undefined || bv === null) ? '' : bv;
      if (av < bv) return -1 * dir; if (av > bv) return 1 * dir; return 0;
    });
  }, [rows, query, sort]);

  const clickSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const activeCount = rows.filter((r) => r.active).length;
  const unassigned = rows.filter((r) => !r.area).length;

  return (
    <div style={{ padding: '20px', maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Users size={22} color="var(--hp-gold)" />
        <h2 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Drivers</h2>
      </div>
      <div style={{ color: 'var(--hp-dim)', fontSize: '.82rem', marginBottom: 16 }}>
        {rows.length} drivers · {activeCount} active
        {unassigned > 0 && <> · <span style={{ color: 'var(--hp-amber)', fontWeight: 700 }}>{unassigned} unassigned</span></>}
        {isAdmin ? ' · you can reveal banking/PIN' : ' · banking/PIN hidden (admin only)'}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 12px', maxWidth: 380 }}>
        <Search size={16} color="var(--hp-dim)" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search login, name, company, ABN…"
          style={{ border: 'none', outline: 'none', background: 'transparent', color: 'var(--hp-text)', fontSize: '.9rem', width: '100%', fontFamily: 'var(--font-body)' }} />
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.4)', color: '#ef4444', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 14, fontSize: '.85rem', display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--hp-dim)', padding: 40, textAlign: 'center' }}>Loading drivers…</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={{ ...th, cursor: 'default' }}>Photo</th>
                {COLS.map((c) => (
                  <th key={c.key} style={th} onClick={() => clickSort(c.key)}>
                    {c.label}{sort.key === c.key ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
                <th style={{ ...th, cursor: 'default' }}>Rego</th>
                {isAdmin && <th style={{ ...th, cursor: 'default' }}>Banking</th>}
                {isAdmin && <th style={{ ...th, cursor: 'default' }}>Login</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const rb = !!busy[r.login];
                const sens = reveal[r.login];
                return (
                  <React.Fragment key={r.login}>
                    <tr style={{ opacity: r.active ? 1 : 0.55 }}>
                      <td style={td}>
                        <DriverAvatar
                          login={r.login}
                          photo={photos[r.login]}
                          size={34}
                          editable
                          onUploaded={(url) => setPhotos((p) => ({ ...p, [r.login]: url }))}
                        />
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{r.login}</td>
                      <td style={td}>{r.name || '—'}</td>
                      <td style={td}>{r.company || '—'}</td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{r.abn || '—'}</td>
                      <td style={td}>{r.mobile || '—'}</td>
                      <td style={{ ...td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email || '—'}</td>
                      <td style={td}>
                        <select value={r.area || ''} disabled={rb} onChange={(e) => assignArea(r.login, e.target.value)}
                          style={{ fontSize: '.78rem', fontWeight: 700, padding: '3px 6px', borderRadius: 6, cursor: rb ? 'wait' : 'pointer',
                            border: `1px solid ${r.area ? AREA_COLOR[r.area] : 'var(--hp-amber)'}`,
                            color: r.area ? AREA_COLOR[r.area] : 'var(--hp-amber)', background: 'transparent' }}>
                          <option value="">Unassigned</option>
                          {AREA_OPTIONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                        </select>
                      </td>
                      <td style={td}>{r.type || '—'}</td>
                      <td style={td}>
                        <button onClick={() => toggleActive(r.login, !r.active)} disabled={rb} title={r.active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: 'none', cursor: rb ? 'wait' : 'pointer', fontWeight: 700, fontSize: '.75rem',
                            background: r.active ? 'rgba(31,138,76,.15)' : 'rgba(120,120,120,.15)', color: r.active ? 'var(--hp-green)' : 'var(--hp-dim)' }}>
                          {r.active ? <Check size={13} /> : <X size={13} />} {r.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{r.rego || '—'}</td>
                      {isAdmin && (
                        <td style={td}>
                          <button onClick={() => toggleReveal(r.login)} disabled={rb}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--hp-border)', cursor: rb ? 'wait' : 'pointer', fontSize: '.75rem', background: 'transparent', color: 'var(--hp-dim)' }}>
                            {sens ? <EyeOff size={13} /> : <Eye size={13} />} {sens ? 'Hide' : 'Reveal'}
                          </button>
                        </td>
                      )}
                      {isAdmin && (
                        <td style={td}>
                          <button onClick={() => setEditing(r)} disabled={rb} title="Edit login ID / PIN"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--hp-border)', cursor: rb ? 'wait' : 'pointer', fontSize: '.75rem', background: 'transparent', color: 'var(--hp-dim)' }}>
                            <KeyRound size={13} /> Edit
                          </button>
                        </td>
                      )}
                    </tr>
                    {isAdmin && sens && (
                      <tr>
                        <td colSpan={COLS.length + (isAdmin ? 3 : 1)} style={{ padding: '10px 14px', background: 'var(--hp-bg)', borderBottom: '1px solid var(--hp-border)' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, fontSize: '.8rem' }}>
                            {[['Account', sens.accountName], ['BSB', sens.bsb], ['Account no.', sens.accNumber], ['PIN', sens.pin], ['GST', sens.gstRegistered], ['Lease fee', sens.leaseFee], ['Lease rate', sens.leaseRate], ['Lease cap', sens.leaseCap]]
                              .filter(([, v]) => v !== '' && v !== undefined && v !== null)
                              .map(([k, v]) => (
                                <div key={k}>
                                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.62rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hp-dim)' }}>{k}</div>
                                  <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-text)', fontWeight: 700 }}>{v}</div>
                                </div>
                              ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!filtered.length && (
                <tr><td colSpan={COLS.length + (isAdmin ? 3 : 1)} style={{ ...td, textAlign: 'center', color: 'var(--hp-dim)', padding: 30 }}>No drivers match “{query}”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && editing && (
        <CredentialsModal
          driver={editing}
          onClose={() => setEditing(null)}
          onPinSaved={() => setReveal({})}   // drop any revealed PIN so it can't show a stale value
          onRenamed={(from, to, res) => {
            setRows((rs) => rs.map((r) => (r.login === from ? { ...r, login: to } : r)));
            setReveal({});
            if (res.errors && res.errors.length) setError(res.error);
          }}
        />
      )}
    </div>
  );
}
